/**
 * MarkdownBlock — 渲染 markdown 文本块。
 *
 * 使用 ReactMarkdown + remark-gfm + rehype-highlight。
 * TOC heading 组件通过 createTocHeadingComponents 注入 data-toc-id。
 */

import type { SourceSpan } from "@magic-md/core";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import remarkDefinitionList, { defListHastHandlers } from "remark-definition-list";
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
import { remarkAlert } from "remark-github-blockquote-alert";
import remarkMath from "remark-math";
import {
    assetRefUrlTransform,
    createAssetRefRemarkPlugin,
    withAssetRefMarkdownComponents,
} from "../asset-refs";
import {
    createAnnotationSourceRehypePlugin,
} from "../../annotations/markdown-rehype";
import { useAnnotationContextOptional } from "../../annotations/AnnotationProvider";
import { useDocContext } from "../contexts/DocContext";
import { createTocHeadingComponents, normalizeRenderedMarkdown } from "../utils";

export function MarkdownBlock({
    content,
    blockIndex,
    source,
}: {
    content: string;
    blockIndex: number;
    source?: SourceSpan;
}) {
    const { documentPath, importDocuments, openFile, openExternal } = useDocContext();
    const annotationContext = useAnnotationContextOptional();
    const annotationRehypePlugin =
        source && annotationContext
            ? createAnnotationSourceRehypePlugin({
                documentPath: annotationContext.documentPath,
                content,
                source,
                annotations: annotationContext.annotations,
            })
            : null;
    const rehypePlugins = annotationRehypePlugin
        ? [annotationRehypePlugin, rehypeKatex, rehypeHighlight]
        : [rehypeKatex, rehypeHighlight];

    return (
        <ReactMarkdown
            remarkPlugins={[
                remarkGfm,
                [remarkFrontmatter, ["yaml", "toml"]],
                remarkDefinitionList,
                remarkMath,
                remarkAlert,
                createAssetRefRemarkPlugin(documentPath, importDocuments),
            ]}
            urlTransform={assetRefUrlTransform}
            remarkRehypeOptions={{ handlers: defListHastHandlers }}
            rehypePlugins={rehypePlugins}
            components={withAssetRefMarkdownComponents(
                documentPath,
                createTocHeadingComponents(blockIndex),
                { openFile, openExternal },
                importDocuments,
            )}
        >
            {normalizeRenderedMarkdown(content)}
        </ReactMarkdown>
    );
}
